<template>
  <div ref="container" class="floating-horizontal-scrollbar-host">
    <slot />
    <div
      v-show="showScrollbar"
      class="floating-horizontal-scrollbar"
      :style="floatingScrollbarStyle"
    >
      <div ref="floatingScrollbar" class="floating-horizontal-scrollbar__scroller">
        <div
          class="floating-horizontal-scrollbar__spacer"
          :style="{ width: `${scrollbarWidth}px` }"
        />
      </div>
    </div>
  </div>
</template>

<script>
export default {
  name: "FloatingHorizontalScrollbar",
  props: {
    scrollWrapperSelector: {
      type: String,
      default: '.v-data-table__wrapper',
    },
    scrollParentSelector: {
      type: String,
      default: null,
    },
    bottom: {
      type: [Number, String],
      default: 0,
    },
    zIndex: {
      type: [Number, String],
      default: 4,
    },
  },
  data() {
    return {
      tableScrollWrapper: null,
      scrollParent: null,
      scrollbarWidth: 0,
      showScrollbar: false,
      floatingScrollbarStyle: {},
      isSyncingTableScroll: false,
      isSyncingFloatingScroll: false,
      updateFrame: null,
      updateFrameType: null,
      floatingScrollbarReady: false,
    }
  },
  mounted() {
    this.$nextTick(() => {
      this.bindFloatingScrollbar()
      this.bindScrollParent()
      this.bindTableScrollWrapper()
      window.addEventListener('resize', this.refresh)
      this.refresh()
    })
  },
  beforeDestroy() {
    this.teardown()
  },
  methods: {
    refresh() {
      this.$nextTick(this.scheduleUpdate)
    },
    scheduleUpdate() {
      if (this.updateFrame) return

      const update = () => {
        this.updateFrame = null
        this.updateFrameType = null
        this.updateScrollbar()
      }

      if (window.requestAnimationFrame) {
        this.updateFrameType = 'animation'
        this.updateFrame = window.requestAnimationFrame(update)
      } else {
        this.updateFrameType = 'timeout'
        this.updateFrame = window.setTimeout(update, 16)
      }
    },
    updateScrollbar() {
      this.bindScrollParent()
      this.bindTableScrollWrapper()

      const wrapper = this.tableScrollWrapper
      const container = this.$refs.container
      if (!wrapper || !container) {
        this.showScrollbar = false
        return
      }

      const containerRect = container.getBoundingClientRect()
      const wrapperRect = wrapper.getBoundingClientRect()
      const viewportHeight = window.innerHeight || document.documentElement.clientHeight
      const isTableVisible = wrapperRect.bottom > 0 && wrapperRect.top < viewportHeight
      const isNativeScrollbarVisible = wrapperRect.bottom <= viewportHeight

      this.scrollbarWidth = wrapper.scrollWidth
      this.showScrollbar = wrapper.scrollWidth > wrapper.clientWidth && isTableVisible && !isNativeScrollbarVisible
      this.floatingScrollbarStyle = {
        left: `${Math.max(containerRect.left, 0)}px`,
        right: `${Math.max(window.innerWidth - containerRect.right, 0)}px`,
        bottom: this.formatCssSize(this.bottom),
        zIndex: this.zIndex,
      }

      if (this.$refs.floatingScrollbar) {
        this.$refs.floatingScrollbar.scrollLeft = wrapper.scrollLeft
      }
    },
    bindFloatingScrollbar() {
      if (this.floatingScrollbarReady || !this.$refs.floatingScrollbar) return

      this.$refs.floatingScrollbar.addEventListener('scroll', this.syncTableScrollbar)
      this.floatingScrollbarReady = true
    },
    bindTableScrollWrapper() {
      const wrapper = this.$el.querySelector(this.scrollWrapperSelector)
      if (wrapper === this.tableScrollWrapper) return

      if (this.tableScrollWrapper) {
        this.tableScrollWrapper.removeEventListener('scroll', this.syncFloatingScrollbar)
      }

      this.tableScrollWrapper = wrapper
      if (this.tableScrollWrapper) {
        this.tableScrollWrapper.addEventListener('scroll', this.syncFloatingScrollbar)
      }
    },
    bindScrollParent() {
      const parent = this.findScrollParent()
      if (parent === this.scrollParent) return

      if (this.scrollParent) {
        this.scrollParent.removeEventListener('scroll', this.refresh)
      }

      this.scrollParent = parent
      if (this.scrollParent) {
        this.scrollParent.addEventListener('scroll', this.refresh, { passive: true })
      }
    },
    findScrollParent() {
      if (!this.scrollParentSelector) return window

      return this.$el.closest(this.scrollParentSelector) || document.querySelector(this.scrollParentSelector) || window
    },
    syncFloatingScrollbar() {
      if (this.isSyncingTableScroll || !this.$refs.floatingScrollbar || !this.tableScrollWrapper) return

      this.isSyncingFloatingScroll = true
      this.$refs.floatingScrollbar.scrollLeft = this.tableScrollWrapper.scrollLeft
      this.$nextTick(() => {
        this.isSyncingFloatingScroll = false
      })
    },
    syncTableScrollbar() {
      if (this.isSyncingFloatingScroll || !this.$refs.floatingScrollbar || !this.tableScrollWrapper) return

      this.isSyncingTableScroll = true
      this.tableScrollWrapper.scrollLeft = this.$refs.floatingScrollbar.scrollLeft
      this.$nextTick(() => {
        this.isSyncingTableScroll = false
      })
    },
    formatCssSize(value) {
      return typeof value === 'number' ? `${value}px` : value
    },
    teardown() {
      if (this.updateFrame) {
        if (this.updateFrameType === 'animation') {
          window.cancelAnimationFrame(this.updateFrame)
        } else {
          window.clearTimeout(this.updateFrame)
        }
        this.updateFrame = null
        this.updateFrameType = null
      }
      if (this.tableScrollWrapper) {
        this.tableScrollWrapper.removeEventListener('scroll', this.syncFloatingScrollbar)
      }
      if (this.$refs.floatingScrollbar) {
        this.$refs.floatingScrollbar.removeEventListener('scroll', this.syncTableScrollbar)
      }
      if (this.scrollParent) {
        this.scrollParent.removeEventListener('scroll', this.refresh)
      }
      window.removeEventListener('resize', this.refresh)
    },
  },
}
</script>

<style scoped>
.floating-horizontal-scrollbar-host {
  position: relative;
}

.floating-horizontal-scrollbar {
  position: fixed;
  padding: 6px 10px 8px;
  background: linear-gradient(to bottom, rgba(255, 255, 255, 0), #fff 35%);
  border-top: 1px solid #e0e0e0;
}

.floating-horizontal-scrollbar__scroller {
  overflow-x: auto;
  overflow-y: hidden;
}

.floating-horizontal-scrollbar__spacer {
  height: 1px;
}

.floating-horizontal-scrollbar__scroller::-webkit-scrollbar {
  height: 12px;
}

.floating-horizontal-scrollbar__scroller::-webkit-scrollbar-track {
  background: #edf1f5;
  border-radius: 999px;
}

.floating-horizontal-scrollbar__scroller::-webkit-scrollbar-thumb {
  background: #90a4ae;
  border: 2px solid #edf1f5;
  border-radius: 999px;
}

.floating-horizontal-scrollbar__scroller::-webkit-scrollbar-thumb:hover {
  background: #607d8b;
}
</style>
